import { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useRouter } from 'expo-router';
import Toast from 'react-native-toast-message';
import Chip from '@/components/General/Chip';
import Callout from '@/components/General/Callout';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import CaregiverRow from '@/components/FollowUp/CaregiverRow';
import TouchTimeline from '@/components/FollowUp/TouchTimeline';
import LogTouchSheet from '@/components/FollowUp/LogTouchSheet';
import CreateFollowUpTaskSheet from '@/components/FollowUp/CreateFollowUpTaskSheet';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import MealRoster from '@/components/FollowUp/Care/MealRoster';
import MealDatesSheet from '@/components/FollowUp/Care/MealDatesSheet';
import CloseCareSheet from '@/components/FollowUp/Care/CloseCareSheet';
import EditCareDetailsSheet from '@/components/FollowUp/Care/EditCareDetailsSheet';
import { Mutation } from '@/utils/react-query/Mutations';
import { extractFollowUpCode } from '@/data/followUp';
import { parseTimeFrame } from '@/utils/dater';
import { formatMealDate } from '@/utils/followUpCare';
import { getTaskPurposeIcon, getTaskPurposeLabel } from '@/utils/constants/followUp';

/**
 * CareEpisodeView — the template-free care episode layout (§25.9): the
 * FollowUpCareEpisodes row IS the episode, so the team sees a shared
 * activity surface — the short context up front, visit preferences, the
 * primary caregiver (or a visible Needs Owner state), recent touches with
 * actor + time, planned/assigned work, and the meal roster.
 *
 * `episode` is the follow_up_get_care_episode episode object
 * ({careEpisodeID, personID, personName, context, careDetails, status,
 * startedAt, closedAt, closureNote}); `care` carries the rest of the RPC
 * projection ({primaryCaregiver, recentTouches, plannedTasks, mealSlots}).
 *
 * Closing is explicit (primary caregiver or Manage) through the
 * keep-or-cancel review sheet — all-delivered meals never close the
 * episode on their own (C14).
 */
const CareEpisodeView = ({ organizationID, episode, care, loggedInUser, canManage }) => {
    const router = useRouter();
    const personID = episode?.personID;
    const careEpisodeID = episode?.careEpisodeID;
    const careDetails = episode?.careDetails || {};
    const isActive = episode?.status === 'ACTIVE';
    const isPrimaryCaregiver = !!care?.primaryCaregiver?.personID
        && care.primaryCaregiver.personID === loggedInUser?.id;
    const canAdminister = canManage || isPrimaryCaregiver;
    const needsOwner = !care?.primaryCaregiver?.personID;

    const [logOpen, setLogOpen] = useState(false);
    const [correctingTouch, setCorrectingTouch] = useState(null);
    const [taskOpen, setTaskOpen] = useState(false);
    const [mealDatesOpen, setMealDatesOpen] = useState(false);
    const [editDetailsOpen, setEditDetailsOpen] = useState(false);
    const [closeOpen, setCloseOpen] = useState(false);
    const [assigningTask, setAssigningTask] = useState(null);

    const assignMutation = Mutation.followUp.assignTask({
        onSuccess: () => {
            Toast.show({ type: 'success', text2: 'Task assigned' });
            setAssigningTask(null);
        },
        onError: (error) => {
            const code = error?.followUpCode ?? extractFollowUpCode(error);
            if (code === 'FOLLOW_UP_CONFLICT') {
                Toast.show({ type: 'info', text2: 'The assignment changed just now. The latest state is loaded.' });
            } else if (!error?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to assign the task.' });
            }
        },
    });

    // The care block's touches use touchID; TouchTimeline keys on id.
    const touches = (care?.recentTouches || []).map((t) => ({ ...t, id: t.touchID }));
    const plannedTasks = care?.plannedTasks || [];

    const detailRows = [
        { key: 'location', icon: 'location-outline', label: 'Location', value: careDetails.location },
        { key: 'locationInstructions', icon: 'navigate-outline', label: 'Getting there', value: careDetails.locationInstructions },
        { key: 'coordinationContact', icon: 'call-outline', label: 'Coordinate through', value: careDetails.coordinationContact },
        { key: 'reviewDate', icon: 'calendar-outline', label: 'Review date', value: careDetails.reviewDate ? formatMealDate(careDetails.reviewDate) : null },
        { key: 'deliveryInstructions', icon: 'basket-outline', label: 'Delivery instructions', value: careDetails.deliveryInstructions },
    ].filter((r) => !!r.value);

    return (
        <View style={styles.wrap}>
            {episode?.status === 'CLOSED' && (
                <Callout
                    type="info"
                    title="Care closed"
                    message={`This episode ended${episode.closedAt ? ` ${parseTimeFrame(episode.closedAt)}` : ''}. Its history is kept.${episode.closureNote ? ` Note: ${episode.closureNote}` : ''}`}
                />
            )}
            {episode?.status === 'CANCELLED' && (
                <Callout
                    type="info"
                    message="This care episode was cancelled. Its history is kept."
                />
            )}

            {/* Short episode context (§25.9 — "Hospital stay"), intake text,
                not a managed template/type. */}
            {!!episode?.context && (
                <View style={styles.contextRow} testID="care-context">
                    <Unicon name="heart-outline" size={16} colorVariant="paths" />
                    <Text style={styles.contextText}>{episode.context}</Text>
                </View>
            )}

            {/* Visit preferences sit ahead of every action (§13.3 — "visit
                preferences prominently"). Manage/primary caregiver can edit
                the details in place on an ACTIVE episode (same gate as
                follow_up_close_care); the card still renders for them when
                empty so the affordance exists before intake fields are set. */}
            {(!!careDetails.visitPreferences || detailRows.length > 0 || (canAdminister && isActive)) && (
                <View style={styles.card} testID="care-details-card">
                    {canAdminister && (
                        <View style={styles.cardHeaderRow}>
                            <Text style={styles.detailLabel}>Care details</Text>
                            <Pressable
                                testID="care-details-edit"
                                accessibilityRole="button"
                                accessibilityLabel="Edit care details"
                                accessibilityState={{ disabled: !isActive }}
                                disabled={!isActive}
                                hitSlop={8}
                                onPress={() => setEditDetailsOpen(true)}
                            >
                                <Unicon
                                    name="create-outline"
                                    size={17}
                                    colorVariant={isActive ? 'paths' : 'textTertiary'}
                                />
                            </Pressable>
                        </View>
                    )}
                    {!careDetails.visitPreferences && detailRows.length === 0 && (
                        <Text style={styles.emptyText}>No care details recorded yet.</Text>
                    )}
                    {!!careDetails.visitPreferences && (
                        <View style={styles.prefsRow}>
                            <Unicon name="heart-outline" size={18} colorVariant="paths" />
                            <Text style={styles.prefsText}>{careDetails.visitPreferences}</Text>
                        </View>
                    )}
                    {detailRows.map((row) => (
                        <View key={row.key} style={styles.detailRow}>
                            <Unicon name={row.icon} size={15} colorVariant="textSecondary" />
                            <View style={styles.detailBody}>
                                <Text style={styles.detailLabel}>{row.label}</Text>
                                <Text style={styles.detailValue}>{row.value}</Text>
                            </View>
                        </View>
                    ))}
                </View>
            )}

            <View style={styles.card}>
                <CaregiverRow
                    organizationID={organizationID}
                    personID={personID}
                    caregiverPersonID={care?.primaryCaregiver?.personID ?? null}
                    canManage={canManage}
                />
                {needsOwner && (
                    <View style={styles.needsOwnerRow} testID="care-needs-owner">
                            <Chip icon="person-add-outline" colorVariant="warning">Needs a primary connector</Chip>
                    </View>
                )}
            </View>

            {isActive && (
                <View style={styles.actionsRow}>
                    <SolidButton
                        testID="care-log-touch"
                        size="sm"
                        iconName="add"
                        text="Log Visit or Call"
                        onPress={() => setLogOpen(true)}
                    />
                    <SolidButton
                        testID="care-create-task"
                        size="sm"
                        variant="outlined"
                        type="secondary"
                        iconName="clipboard-outline"
                        text="Plan a Task"
                        onPress={() => setTaskOpen(true)}
                    />
                    {canAdminister && (
                        <SolidButton
                            testID="care-close"
                            size="sm"
                            variant="outlined"
                            type="textSecondary"
                            iconName="archive-outline"
                            text="Close Care"
                            onPress={() => setCloseOpen(true)}
                        />
                    )}
                </View>
            )}

            <Text style={styles.sectionTitle}>Recent Activity</Text>
            <TouchTimeline
                touches={touches}
                loggedInUser={loggedInUser}
                organizationID={organizationID}
                personID={personID}
                onCorrect={isActive ? setCorrectingTouch : null}
            />

            <Text style={styles.sectionTitle}>Planned Visits &amp; Tasks</Text>
            {plannedTasks.length === 0 ? (
                <Text style={styles.emptyText}>No visits or tasks are scheduled yet.</Text>
            ) : (
                <View style={styles.card}>
                    {plannedTasks.map((task) => {
                        const assigneeNames = (task.assignees || []).map((a) => a.name).filter(Boolean);
                        return (
                            <View key={task.taskInstanceID} style={styles.taskRow} testID={`care-task-${task.taskInstanceID}`}>
                                <Pressable
                                    accessibilityRole="button"
                                    style={styles.taskBodyRow}
                                    onPress={() => router.push(`/tasks/instances/${task.taskInstanceID}`)}
                                >
                                    <View style={styles.taskIcon}>
                                        <Unicon name={getTaskPurposeIcon(task.purpose)} size={15} colorVariant="paths" />
                                    </View>
                                    <View style={styles.taskBody}>
                                        <Text style={styles.taskTitle} numberOfLines={1}>
                                            {task.title || getTaskPurposeLabel(task.purpose)}
                                        </Text>
                                        <Text style={styles.taskMeta} numberOfLines={1}>
                                            {[
                                                getTaskPurposeLabel(task.purpose),
                                                task.dueDate ? `Due ${parseTimeFrame(task.dueDate)}` : null,
                                                assigneeNames.length ? assigneeNames.join(', ') : 'Unassigned',
                                            ].filter(Boolean).join(' · ')}
                                        </Text>
                                    </View>
                                </Pressable>
                                {canAdminister && isActive && (
                                    <Pressable
                                        testID={`care-task-assign-${task.taskInstanceID}`}
                                        accessibilityRole="button"
                                        accessibilityLabel="Assign task"
                                        hitSlop={8}
                                        onPress={() => setAssigningTask(task)}
                                    >
                                        <Unicon name="person-add-outline" size={18} colorVariant="textSecondary" />
                                    </Pressable>
                                )}
                            </View>
                        );
                    })}
                </View>
            )}

            <MealRoster
                organizationID={organizationID}
                personID={personID}
                careEpisodeID={careEpisodeID}
                mealSlots={care?.mealSlots || []}
                isActive={isActive}
                canAdminister={canAdminister}
                loggedInPersonID={loggedInUser?.id}
                onEditDates={() => setMealDatesOpen(true)}
            />

            <LogTouchSheet
                isOpen={logOpen}
                onClose={() => setLogOpen(false)}
                organizationID={organizationID}
                personID={personID}
                personName={episode?.personName}
                careEpisodeID={careEpisodeID}
            />
            <LogTouchSheet
                isOpen={!!correctingTouch}
                onClose={() => setCorrectingTouch(null)}
                organizationID={organizationID}
                personID={personID}
                personName={episode?.personName}
                touch={correctingTouch}
            />
            <CreateFollowUpTaskSheet
                isOpen={taskOpen}
                onClose={() => setTaskOpen(false)}
                organizationID={organizationID}
                personID={personID}
                personName={episode?.personName}
                primaryCaregiverPersonID={care?.primaryCaregiver?.personID ?? null}
                careEpisodeID={careEpisodeID}
            />
            <EditCareDetailsSheet
                isOpen={editDetailsOpen}
                onClose={() => setEditDetailsOpen(false)}
                organizationID={organizationID}
                careEpisodeID={careEpisodeID}
                personID={personID}
                personName={episode?.personName}
                careDetails={careDetails}
            />
            <MealDatesSheet
                isOpen={mealDatesOpen}
                onClose={() => setMealDatesOpen(false)}
                organizationID={organizationID}
                personID={personID}
                careEpisodeID={careEpisodeID}
                mealSlots={care?.mealSlots || []}
            />
            <CloseCareSheet
                isOpen={closeOpen}
                onClose={() => setCloseOpen(false)}
                organizationID={organizationID}
                careEpisodeID={careEpisodeID}
                personID={personID}
                personName={episode?.personName}
            />

            {/* Assign picker for planned work — manage or primary caregiver. */}
            {assigningTask && (
                <AssignPicker
                    organizationID={organizationID}
                    personID={personID}
                    careEpisodeID={careEpisodeID}
                    task={assigningTask}
                    onClose={() => setAssigningTask(null)}
                    assignMutation={assignMutation}
                />
            )}
        </View>
    );
};

/** People picker wrapper for assigning an open care task. */
const AssignPicker = ({ organizationID, personID, careEpisodeID, task, onClose, assignMutation }) => {
    return (
        <SheetSelector
            type="people"
            title={`Assign "${task.title || 'task'}" to`}
            open
            onOpen={() => {}}
            onClose={onClose}
            value={null}
            excludeIds={personID ? [personID] : []}
            onChange={(selected) => {
                if (!selected?.id) return;
                assignMutation.mutate({
                    organizationID,
                    taskInstanceID: task.taskInstanceID,
                    assigneePersonID: selected.id,
                    personID,
                    careEpisodeID,
                });
            }}
        >
            <View />
        </SheetSelector>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    wrap: { gap: 10 },
    contextRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: colors.pathsContainer,
        borderRadius: 10,
        paddingHorizontal: 12,
        paddingVertical: 10,
    },
    contextText: { flex: 1, fontSize: 14, fontFamily: fonts.medium, color: colors.text },
    card: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 14,
        gap: 12,
        boxShadow: shadows.card,
    },
    prefsRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
        backgroundColor: colors.pathsContainer,
        borderRadius: 10,
        padding: 12,
    },
    prefsText: { flex: 1, fontSize: 14, fontFamily: fonts.medium, color: colors.text, lineHeight: 20 },
    detailRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
    detailBody: { flex: 1, minWidth: 0 },
    detailLabel: { fontSize: 11, color: colors.textSecondary, fontFamily: fonts.medium },
    detailValue: { fontSize: 14, color: colors.text, marginTop: 1 },
    cardHeaderRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        _web: { cursor: 'default' },
    },
    needsOwnerRow: { flexDirection: 'row' },
    actionsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    sectionTitle: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        marginTop: 4,
    },
    emptyText: { fontSize: 13, color: colors.textSecondary, fontStyle: 'italic', paddingHorizontal: 4 },
    taskRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 4,
    },
    taskBodyRow: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10, _web: { cursor: 'pointer' } },
    taskIcon: {
        width: 30,
        height: 30,
        borderRadius: 8,
        backgroundColor: colors.pathsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    taskBody: { flex: 1, minWidth: 0 },
    taskTitle: { fontSize: 14, fontFamily: fonts.bold, color: colors.text },
    taskMeta: { fontSize: 12, color: colors.textSecondary, marginTop: 1 },
}));

export default CareEpisodeView;
